import { Button } from '@design-system';
import { isInNextSixDays } from '@utils/product';
import { Trans, useTranslation } from 'next-i18next';
import { useMemo, useState } from 'react';

import { useProductCheckoutLink } from '../../../../features/checkout/hooks/useProductCheckoutLink';
import { useDrawButtonLabels } from '../../../../features/draws/hooks/useDrawButtonLabels';
import { useDraws } from '../../../../providers/DrawProvider';
import { CurrencyCode } from '../../../../types/Locations';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import { ProductPrice } from '../../../ProductPrice/ProductPrice';
import ProductDate from '../../ProductDate';
import { DrawEntryButton } from './DrawEntryButton';

export const getOrdinal = (n: number): string => {
  const pr = new Intl.PluralRules('en-UK', { type: 'ordinal' });
  const rule = pr.select(n);

  const suffixes = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
    many: 'th',
    zero: '',
  };

  return suffixes[rule] || suffixes.other;
};

export const AuctionFlow = () => {
  const { t } = useTranslation('product');
  const [showDialog, setShowDialog] = useState(false);
  const { accountAuctionBid, isAccountInDraw, mainDraw } = useDraws();
  const checkoutLink = useProductCheckoutLink();
  const labels = useDrawButtonLabels('mainCta', false, false, true);

  const { label, launchDate } = useMemo(() => {
    if (!mainDraw?.draw.closeAt) {
      return {
        label: '',
        launchDate: undefined,
      };
    }
    const date = new Date(mainDraw?.draw.closeAt);
    if (isInNextSixDays(date)) {
      return {
        label: 'auction.default.subscriptionDate.closingCountdown',
        launchDate: date,
      };
    }
    return {
      label: 'auction.default.subscriptionDate.closingDate',
      launchDate: date,
    };
  }, [mainDraw?.draw.closeAt]);

  if (!mainDraw) {
    return null;
  }

  const { draw: auction } = mainDraw;

  const learnMoreBodyLabel =
    auction.allocation > 1
      ? 'auction.default.learnMore.RankedAuctionBody'
      : 'auction.default.learnMore.SingleAuctionBody';

  const paragraphs = t(learnMoreBodyLabel, {
    returnObjects: true,
  }) as unknown as string[];

  const accountBid = accountAuctionBid(auction.id);
  const entered = isAccountInDraw(auction.id);

  return (
    <>
      <div className="flex w-full flex-col gap-6 text-center">
        <div className="flex w-full flex-1 flex-col">
          <p className="text-center font-bold">{t(label)}</p>
          <p className="text-center">
            {launchDate && <ProductDate date={launchDate} />}
          </p>
        </div>
        {auction.auctionReservePrice &&
          auction.auctionReservePrice.amount !== '0' && (
            <div className="flex w-full flex-1 flex-col">
              <p className="text-center text-neutral-4">
                <ProductPrice
                  currencyCode={
                    auction.auctionReservePrice.currencyCode as CurrencyCode
                  }
                  prefix={t('auction.default.reservePrice')}
                  price={Number(auction.auctionReservePrice.amount)}
                  skipConvertCurrency
                  skipTaxCalculation
                />
              </p>
            </div>
          )}

        {accountBid && (
          <div className="flex w-full flex-1 flex-col">
            <p className="text-center text-neutral-6">
              {t('auction.default.accountBid', {
                amount: accountBid.amount,
                currencyCode: accountBid.currencyCode,
              })}
            </p>
          </div>
        )}
        <div className="flex w-full flex-1 flex-col items-center gap-3">
          <DrawEntryButton
            checkoutLink={entered ? null : checkoutLink}
            entered={entered}
            label={t(labels.normal)}
            mainDraw={mainDraw}
            successLabel={t(labels.success)}
          />
          {entered && checkoutLink && (
            <Button
              as="a"
              fullWidth
              href={checkoutLink}
              label={t('subscriptionCta.auction.updateBid')}
              size="lg"
              variant="tertiary"
            />
          )}
          <button
            className="hyperlink"
            onClick={() => {
              setShowDialog(true);
              // handleHowToClick();
            }}
            type="button"
          >
            {t('auction.default.learnMore.title')}
          </button>
        </div>
      </div>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('auction.default.learnMore.blockChain.title')}
      >
        <div className="flex flex-col gap-3">
          {paragraphs.map((paragraph, index) => (
            <p>
              <Trans
                key={paragraph}
                i18nKey={`auction.default.learnMore.blockChain.body.${index}`}
                values={{
                  size: auction?.allocation,
                  position: `${auction?.allocation}${getOrdinal(
                    auction?.allocation ?? 0,
                  )}`,
                }}
              >
                {paragraph}
              </Trans>
            </p>
          ))}
        </div>
      </SimpleDialog>
    </>
  );
};
